import React, { useContext, useState } from 'react';
import { FunctionPortDefinition, NodeInstance, ShapeValue } from '@glueapp/graph-types';
import { NodeStandardLayout } from '@/GraphEditor/drawingLayer/nodeComposition/NodeStandardLayout';
import { FunctionPortEditingContext } from '../GraphEditor/drawingLayer/contexts/FunctionPortEditingContext';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    ShadcnCheckbox as Checkbox,
    ShadcnInput as Input,
    ShadcnTextarea as Textarea,
} from '../components/migrated';
import { AlertCircle, Plus, Trash2 } from 'lucide-react';
import { ShapeValueEditor } from '../NodeInstanceEditor/ShapeValueEditor/ShapeValueEditor';

interface Props {
    nodeInstance: NodeInstance;
}

export const FunctionInputsNode: React.FC<Props> = ({ nodeInstance }) => {
    const { functionDefinition, updateFunctionInputs } = useContext(FunctionPortEditingContext);
    const [duplicateNames, setDuplicateNames] = useState<Set<string>>(new Set());

    // Get current input port definitions from the function definition
    const inputPortDefinitions: FunctionPortDefinition[] = functionDefinition?.inputPortDefinitions || [
        {
            id: 'default-input',
            name: 'input',
            type: { kind: 'string' },
            optional: false,
            description: 'Function input',
        },
    ];

    // Check for duplicate names whenever the definitions change
    React.useEffect(() => {
        const nameCount = new Map<string, number>();
        const duplicates = new Set<string>();

        inputPortDefinitions.forEach(port => {
            const count = (nameCount.get(port.name) || 0) + 1;
            nameCount.set(port.name, count);
            if (count > 1) {
                duplicates.add(port.name);
            }
        });

        setDuplicateNames(duplicates);
    }, [inputPortDefinitions]);

    const updatePortDefinitions = (newDefinitions: FunctionPortDefinition[]) => {
        if (updateFunctionInputs) {
            updateFunctionInputs(newDefinitions);
        }
    };

    const addInputPort = () => {
        const newPort: FunctionPortDefinition = {
            id: `input-${Date.now()}`,
            name: `input${inputPortDefinitions.length + 1}`,
            type: { kind: 'string' },
            optional: false,
            description: '',
        };

        updatePortDefinitions([...inputPortDefinitions, newPort]);
    };

    const updatePort = (portId: string, updates: Partial<FunctionPortDefinition>) => {
        // Check for duplicate names if name is being updated
        if (updates.name) {
            const isDuplicate = inputPortDefinitions.some(port => port.id !== portId && port.name === updates.name);

            if (isDuplicate) {
                // Don't update if it would create a duplicate name
                alert(`Port name "${updates.name}" already exists. Port names must be unique.`);
                return;
            }
        }

        const updatedDefinitions = inputPortDefinitions.map(port => (port.id === portId ? { ...port, ...updates } : port));
        updatePortDefinitions(updatedDefinitions);
    };

    const removePort = (portId: string) => {
        if (inputPortDefinitions.length <= 1) {
            // Keep at least one port
            return;
        }
        const updatedDefinitions = inputPortDefinitions.filter(port => port.id !== portId);
        updatePortDefinitions(updatedDefinitions);
    };

    return (
        <NodeStandardLayout nodeInstance={nodeInstance}>
            <div className="function-inputs-editor space-y-4 p-4">
                <Button onClick={addInputPort} size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Input
                </Button>
                <div className="space-y-3">
                    {inputPortDefinitions.map(portDef => (
                        <Card key={portDef.id} className="border">
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-sm">Input Port</CardTitle>
                                    {inputPortDefinitions.length > 1 && (
                                        <Button
                                            onClick={() => removePort(portDef.id)}
                                            size="sm"
                                            variant="ghost"
                                            className="text-red-600 hover:text-red-700"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div>
                                    <label className="text-sm font-medium mb-1 block">Port Name</label>
                                    <div className="relative">
                                        <Input
                                            value={portDef.name}
                                            onChange={e => updatePort(portDef.id, { name: e.target.value })}
                                            placeholder="Input port name"
                                            className={`w-full ${duplicateNames.has(portDef.name) ? 'border-red-500' : ''}`}
                                        />
                                        {duplicateNames.has(portDef.name) && (
                                            <div className="flex items-center mt-1 text-red-500 text-xs">
                                                <AlertCircle className="w-3 h-3 mr-1" />
                                                Duplicate name - must be unique
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium mb-1 block">Description</label>
                                    <Textarea
                                        value={portDef.description || ''}
                                        onChange={e => updatePort(portDef.id, { description: e.target.value })}
                                        placeholder="Optional description"
                                        className="w-full"
                                        rows={2}
                                    />
                                </div>

                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id={`optional-${portDef.id}`}
                                        checked={portDef.optional || false}
                                        onCheckedChange={checked => updatePort(portDef.id, { optional: !!checked })}
                                    />
                                    <label htmlFor={`optional-${portDef.id}`} className="text-sm">
                                        Optional
                                    </label>
                                </div>

                                <div>
                                    <label className="text-sm font-medium mb-1 block">Type</label>
                                    <ShapeValueEditor
                                        type={{ kind: 'shape', restriction: undefined }}
                                        value={{ type: portDef.type, isShapeValue: true }}
                                        onChange={(value: ShapeValue) => updatePort(portDef.id, { type: value.type })}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        </NodeStandardLayout>
    );
};
